import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  model,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

import {
  getAllSearchableMenuItems,
  SearchableMenuItem,
} from '../../shared/constants/api.constants';
import { PermissionUtils } from '../../shared/utils/permission.utils';
import { RecentPage, normalizeRecentUrl } from '../recent-pages/recent-pages';
import { RecentPagesService } from '../recent-pages/recent-pages.service';
import { filterMenuSearchHits, filterRecentOutsideMenu, moveMenuSearchIndex } from './menu-search';

type SearchHit = { kind: 'recent'; page: RecentPage } | { kind: 'menu'; item: SearchableMenuItem };

@Component({
  selector: 'app-menu-search',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './menu-search.component.html',
  styleUrl: './menu-search.component.scss',
})
export class MenuSearchComponent implements AfterViewInit {
  /** Two-way: set locally so shortcuts paint without waiting for parent round-trip. */
  readonly open = model(false);

  @ViewChild('queryInput') private queryInput?: ElementRef<HTMLInputElement>;

  query = '';
  activeIndex = 0;
  results: SearchHit[] = [];

  private allowedItems: SearchableMenuItem[] = [];

  constructor(
    private readonly router: Router,
    private readonly permissionUtils: PermissionUtils,
    private readonly recentPages: RecentPagesService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.refreshAllowedItems();
    this.results = this.buildResults('');
  }

  ngAfterViewInit(): void {
    if (this.open()) {
      this.focusQueryInput();
    }
  }

  /** Global shortcut only — navigation keys bind on the focused input. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
      return;
    }
    // Ctrl/⌘F toggles menu search (blocks browser Find). ⌘/Ctrl+K is Wenty.
    if (event.key.toLowerCase() === 'f') {
      event.preventDefault();
      this.setOpen(!this.open());
    }
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = moveMenuSearchIndex(this.activeIndex, 1, this.results.length);
      this.scrollActiveIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = moveMenuSearchIndex(this.activeIndex, -1, this.results.length);
      this.scrollActiveIntoView();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const hit = this.results[this.activeIndex];
      if (hit) {
        this.select(hit);
      }
    }
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.results = this.buildResults(value);
    this.activeIndex = 0;
  }

  select(hit: SearchHit): void {
    const route = hit.kind === 'recent' ? hit.page.url : hit.item.route;
    if (hit.kind === 'recent') {
      const normalized = normalizeRecentUrl(route);
      const allowed = this.allowedItems.some(
        item => normalized === item.route || normalized.startsWith(`${item.route}/`)
      );
      if (!allowed) {
        return;
      }
    }
    void this.router.navigateByUrl(route);
    this.close();
  }

  close(): void {
    this.setOpen(false);
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  trackByKey(_: number, hit: SearchHit): string {
    return hit.kind === 'recent'
      ? `recent:${hit.page.url}`
      : `menu:${hit.item.key}:${hit.item.route}`;
  }

  hitLabel(hit: SearchHit): string {
    return hit.kind === 'recent' ? (hit.page.labels.at(-1) ?? hit.page.breadcrumb) : hit.item.label;
  }

  hitParent(hit: SearchHit): string | undefined {
    if (hit.kind === 'recent') {
      const parents = hit.page.labels.slice(0, -1);
      return parents.length ? parents.join(' › ') : undefined;
    }
    return hit.item.parentLabel;
  }

  /** Section header before first recent / first menu row (for empty or mixed lists). */
  sectionLabelAt(index: number): string | null {
    const hit = this.results[index];
    if (!hit) {
      return null;
    }
    if (index === 0) {
      return hit.kind === 'recent' ? 'Pages' : 'Menu';
    }
    const prev = this.results[index - 1];
    if (prev.kind !== hit.kind) {
      return hit.kind === 'recent' ? 'Pages' : 'Menu';
    }
    return null;
  }

  /** Header search button — same sync path as shortcuts. */
  openFromUi(): void {
    this.setOpen(true);
  }

  private setOpen(next: boolean): void {
    if (next === this.open()) {
      if (next) {
        this.focusQueryInput();
      }
      return;
    }
    if (next) {
      this.refreshAllowedItems();
      this.query = '';
      this.activeIndex = 0;
      this.results = this.buildResults('');
    }
    this.open.set(next);
    // Paint + drop `inert` before focusing (same keydown turn).
    this.cdr.detectChanges();
    if (next) {
      this.focusQueryInput();
    }
  }

  /** Focus after open/`inert` clears — microtask so Ctrl/⌘F lands in the field. */
  private focusQueryInput(): void {
    queueMicrotask(() => {
      const el = this.queryInput?.nativeElement;
      if (!el || !this.open()) {
        return;
      }
      el.focus({ preventScroll: true });
      el.select();
    });
  }

  private buildResults(query: string): SearchHit[] {
    const term = query.trim();
    // Empty query: full menu (parents + children). Search: still show all matches (panel scrolls).
    const menuHits: SearchHit[] = filterMenuSearchHits(this.allowedItems, term).map(item => ({
      kind: 'menu' as const,
      item,
    }));
    // Menu owns list/nav routes — Pages only keeps deep visits (no URL duplicates).
    const recentHits: SearchHit[] = filterRecentOutsideMenu(
      this.recentPages.search(term, 8),
      this.allowedItems.map(item => item.route),
      normalizeRecentUrl
    ).map(page => ({ kind: 'recent' as const, page }));
    return [...recentHits, ...menuHits];
  }

  private refreshAllowedItems(): void {
    this.allowedItems = getAllSearchableMenuItems().filter(item => {
      if (item.allowAuthenticated) {
        return true;
      }
      // Deny-closed: leaf items without a permissions list stay hidden from search.
      if (!item.permissions?.length) {
        return false;
      }
      return item.permissions.some(permission => this.permissionUtils.hasPermission(permission));
    });
  }

  private scrollActiveIntoView(): void {
    queueMicrotask(() => {
      document.getElementById(`menu-search-option-${this.activeIndex}`)?.scrollIntoView({
        block: 'nearest',
      });
    });
  }
}
